import { useSyncExternalStore } from 'react'
import {
  canRequestIOSNotifications,
  getCurrentPushSubscription,
  getNotificationPermission,
  isAppInstalled,
  isIOSOrIPadOS,
  isMacOS,
  isPushNotificationSupported,
  syncPushSubscription,
} from '../services/pushNotification'

/**
 * Push notifications on this browser, as one store every component reads.
 *
 * The profile toggle, the gym bell, the dashboard card and the ask after a
 * follow all have to agree: turning push on in one of them must take the
 * others' offers away, and a "Block" in the browser's own prompt must show up
 * everywhere at once. Each used to hold its own copy, checked once on mount.
 * Re-checked when the page comes back into view and when the browser reports a
 * permission change, since both can be changed from outside the page.
 *
 * The browser's subscription says nothing about *who* it delivers to: a phone
 * shared by several accounts has one endpoint registered by each of them (see
 * docs/PUSH_OPT_IN_PLAN.md). `registration` is the server's answer for the
 * signed-in account, asked once per session (`ensureRegistration`).
 */

export interface PushState {
  loading: boolean
  isSupported: boolean
  permission: NotificationPermission
  /** iOS and iPadOS deliver push only to the installed app. */
  needsAppInstall: boolean
  isIOSOrIPadOS: boolean
  isMacOS: boolean
  isAppInstalled: boolean
  /** This browser's push endpoint, whoever registered it. */
  endpoint: string | null
  /** Whether `endpoint` is registered for `userId`. Null until asked. */
  registration: { userId: string; endpoint: string; registered: boolean } | null
}

const INITIAL: PushState = {
  loading: true,
  isSupported: false,
  permission: 'default',
  needsAppInstall: false,
  isIOSOrIPadOS: false,
  isMacOS: false,
  isAppInstalled: false,
  endpoint: null,
  registration: null,
}

let state: PushState = INITIAL
let listening = false
let refreshing: Promise<void> | null = null
let registering: Promise<void> | null = null
const subscribers = new Set<() => void>()

function setState(next: Partial<PushState>) {
  state = { ...state, ...next }
  subscribers.forEach((notify) => notify())
}

export function getPushState(): PushState {
  return state
}

/** Reads the browser again. Concurrent calls share one check. */
export function refreshPushState(): Promise<void> {
  if (refreshing) return refreshing

  refreshing = (async () => {
    const supported = isPushNotificationSupported()
    const iOS = isIOSOrIPadOS()
    const permission = getNotificationPermission()
    const browser = {
      isSupported: supported,
      isIOSOrIPadOS: iOS,
      isMacOS: isMacOS(),
      isAppInstalled: isAppInstalled(),
      permission,
      // A permission granted some other way still lets the page subscribe.
      needsAppInstall: iOS && !canRequestIOSNotifications() && permission !== 'granted',
    }

    let endpoint: string | null = null
    if (supported && permission === 'granted') {
      try {
        endpoint = (await getCurrentPushSubscription())?.endpoint ?? null
      } catch {
        endpoint = null
      }
    }

    const registration = state.registration?.endpoint === endpoint ? state.registration : null
    setState({ ...browser, endpoint, registration, loading: false })
  })().finally(() => {
    refreshing = null
  })

  return refreshing
}

/**
 * Asks the server whether this browser is registered for `userId`, once per
 * account and endpoint. Asking also keeps the registration counting as a live
 * device, so it is never asked of a browser with nothing to ask about.
 */
export function ensureRegistration(userId: string): Promise<void> {
  const { endpoint, permission, registration } = state
  if (!endpoint || permission !== 'granted') return Promise.resolve()
  if (registration?.userId === userId && registration.endpoint === endpoint) return Promise.resolve()
  if (registering) return registering

  registering = syncPushSubscription(endpoint)
    .then((registered) => setState({ registration: { userId, endpoint, registered } }))
    .catch(() => {
      // Unknown: left to the next check rather than guessed.
    })
    .finally(() => {
      registering = null
    })

  return registering
}

/** What the page now knows after this account subscribed or detached. */
export function setRegistration(userId: string, endpoint: string | null, registered: boolean) {
  setState({ endpoint, registration: endpoint ? { userId, endpoint, registered } : null })
}

function listen() {
  if (listening || typeof window === 'undefined') return
  listening = true

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refreshPushState()
  })

  // Not every browser lets the page watch the permission (Safari before 16).
  navigator.permissions
    ?.query({ name: 'notifications' })
    .then((status) => {
      status.onchange = () => void refreshPushState()
    })
    .catch(() => {})
}

function subscribe(notify: () => void) {
  listen()
  subscribers.add(notify)
  return () => {
    subscribers.delete(notify)
  }
}

export function usePushState(): PushState {
  return useSyncExternalStore(subscribe, getPushState)
}

/** Test seam: forget everything read so far. */
export function resetPushState() {
  state = INITIAL
  refreshing = null
  registering = null
}
